import React, { useState, useEffect, ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

import type { AssemblyPrice, AssemblyShadePayload, QuoteDataObject, QuoteTypeNames } from '../../../powershadesApiTypes';

import SpinningWheel from '../../../Parts/SpinningWheel';
import Money from '../../../Parts/Money';
import apiCalls from '../../../PowerShadesAPIFunctions';
import Button from '../../../Parts/Button';

type ShadePriceBreakdownProps = {
	quoteData: QuoteDataObject;
	sequenceId: number;
	quoteType: QuoteTypeNames;
	done: any;
}

const ShadePriceBreakdown = ({
	quoteData, sequenceId, quoteType, done
}: ShadePriceBreakdownProps) => {
	const [assemblyPricing, setAssemblyPricing] = useState<AssemblyPrice | undefined>(undefined);
	const [multiplier, setMultiplier] = useState(1);
	const [shade,] = useState(quoteData.shadeSubstitute(sequenceId));
	const [symbol,] = useState('$');
	const [iso, setIso] = useState('USD');

	useEffect(() => {
		if (!shade || shade === undefined || assemblyPricing !== undefined) return;
		const shadePrice = quoteData?.shadeAssemblies?.Items.find((s) => s.sequence_id == sequenceId);
		apiCalls
			.calculateAssemblyPrice([shadePrice] as unknown as AssemblyShadePayload, quoteData.QuoteId)
			.then((resp) => setAssemblyPricing(resp.data));
	}, [shade]);

	const costToggle = () => {
		let newIso = 'USD';
		let newMultiplier = 1;

		if (iso === 'USD') {
			newIso = 'CAD';
			newMultiplier = 1.4;
		} else {
			newIso = 'USD';
			newMultiplier = 1;
		}
		setMultiplier(newMultiplier);
		setIso(newIso);
	};

	if (quoteData.Money.msrp === undefined) throw new Error('MSRP is undefined');
	const msrps = quoteData.Money.msrp.totals;
	let loading = true;

	if (msrps === undefined) throw new Error('MSRP Totals is undefined');
	// ? can we hard code these into the types? 
	const dealerMultiplier = msrps.find((m) => m.name === 'dealer')?.multiplier?.multiplier;
	const distributorMultiplier = msrps.find((m) => m.name === 'distributor')?.multiplier?.multiplier;

	// ! Needs to be confirmed that this won't throw errors for non admins
	if (distributorMultiplier === undefined || dealerMultiplier === undefined) throw new Error('Multiplier is undefined');

	// const shadeCount = shade.data().shades.length;

	const isOutdoor = shade.isOutdoor();

	const rows = [
		{ access: 'labels', label: 'Labels' },
		{ access: 'fabric', label: 'Fabric' },
		{ access: 'hembar', label: 'Hembar' },
		{ access: 'motor', label: 'Motor' },
		{ access: 'header', label: 'Header' },
		{
			access: 'channels',
			label: 'Channels',
			extras: <FontAwesomeIcon icon={faInfoCircle} title="Side and Sill" />,
		},
		{ access: 'tube', label: 'Tube' },
		{ access: 'adds', label: 'Shade Adds' },
		{ access: 'hwk', label: 'Hardware Kit' },
		{ access: 'hidden_shipping', label: 'Hidden Shipping' },
		{ access: 'total', label: 'Total' },
	];

	const columns = [
		{ access: 'labels', label: 'Labels' },
		{ access: 'cost', label: 'Cost' },
		{ access: 'base_sale_price', label: 'Distributor Price' },
		{ access: 'dealer_sale_price', label: 'Dealer Price' },
		{ access: 'msrp', label: 'MSRP' },
	];

	const itemSets: (ReactNode | number)[][] = [];

	// const totals = {};

	// ! This does not exist in the API response
	// Yes it does. Don't remove.
	const pricingErrors = assemblyPricing?.prices[0]?.pricingData?.errors ?? [];

	const prettify = (numberToFormat: number): number | '-' => {
		if (numberToFormat === 0.0) {
			return '-';
		}
		return numberToFormat;
	};

	if (assemblyPricing
		&& pricingErrors.length === 0
		&& !isOutdoor) {
		const costBreakdown = assemblyPricing.prices[0].pricingData?.priceData?.cost_breakdown;

		const data = assemblyPricing.prices[0].pricingData;

		let channelCost = 0;
		let channelMsrp = 0;

		rows.forEach((row) => {
			const itemSet : (ReactNode | number)[] = [];

			if (row.access === 'labels') {
				columns.forEach((column) => itemSet.push(column.label));
			} else if (row.access === 'total') {
				const { cost } = data;
				const { msrp } = data;

				itemSet.push(row.label);
				itemSet.push(`${prettify(cost)}`);
				itemSet.push(`${prettify(msrp * distributorMultiplier)}`);
				itemSet.push(`${prettify(msrp * dealerMultiplier)}`);
				itemSet.push(`${prettify(msrp)}`);
			} else if (row.access === 'channels') {
				channelCost = costBreakdown.total_side_channel_cost + costBreakdown.total_sill_channel_cost;
				channelMsrp = costBreakdown.total_side_channel_msrp + costBreakdown.total_sill_channel_msrp;
				itemSet.push(row.label);
				itemSet.push(`${prettify(channelCost)}`);
				itemSet.push(`${prettify(channelMsrp * distributorMultiplier)}`);
				itemSet.push(`${prettify(channelMsrp * dealerMultiplier)}`);
				itemSet.push(`${prettify(channelMsrp)}`);
			} else {
				const accessBase = `total_${row.access}_`;

				const cost = costBreakdown[`${accessBase}cost`];
				const msrp = costBreakdown[`${accessBase}msrp`];

				itemSet.push(row.label);
				itemSet.push(`${prettify(cost)}`);
				itemSet.push(`${prettify(msrp * distributorMultiplier)}`);
				itemSet.push(`${prettify(msrp * dealerMultiplier)}`);
				itemSet.push(`${prettify(msrp)}`);
			}
			itemSets.push(itemSet);
		});
		loading = false;
	}

	const itemRows = itemSets.map((is, index) => {
		const cells = is.map((i, index2) => {
			const isNumber = Number.isNaN(Number(i)) === false;
			if (!isNumber) {
				if (is[5] && i === is[0]) {
					return (
						<th key={`kh-${index}${index2}`}>
							{i}
							&nbsp;
							{is[5]}
						</th>
					);
				}
				return <th key={`kh-${index}${index2}`}>{i}</th>;
			}
			const num = (i ?? 0) as number;
			return (
				<td key={`m-${index}${index2}`}>
					<Money value={num * multiplier} symbol={symbol} />
				</td>
			);
		});

		return <tr key={`ktr-${index}`}>{cells}</tr>;
	});

	const table = (
		<table cellSpacing="0">
			<tbody>{itemRows}</tbody>
		</table>
	);

	const errorStr = isOutdoor
		? "This functionality isn't currently available on Outdoor Shades." 
		: pricingErrors.length != 0
		? pricingErrors
		 : '';

	if (errorStr) {
		loading = false;
	}

	const title = `Internal Cost Breakdown ${errorStr ? `(${errorStr})` : ''}`;
	return (
		<div>
			<h2 className={`text-${quoteType}`} style={{ textAlign: 'center' }}>
				{title}
			</h2>
			<h3 className={`text-${quoteType}`}>
				{shade.val('room_name')}
				&nbsp; -{shade.val('shade_name')}
			</h3>
			{!loading ? (
				<div>
					{table}
					{/* {pricingErrors} */}
					<div className="row">
						<div className="col-6">
							<Button color={quoteType ?? 'green'} onClick={costToggle}>
								{iso}
							</Button>
						</div>
						{/* <Button
										color={quoteType ?? 'green'}
										disabled={
											!pricing || pricingErrors.length != 0
										}
										onClick={() => {}}
									>
										Fix
									</Button> */}
						<div className="col-6">
							<div className="d-flex justify-content-end">
								<Button color={quoteType ?? 'green'} onClick={done}>
									Done
								</Button>
							</div>
						</div>
					</div>
				</div>
			) : (
				<div style={{ textAlign: 'center' }}>
					<SpinningWheel
						style={{
							fontSize: '3em',
							marginLeft: '1em',
							marginTop: '0.5em',
						}}
						className={`text-${quoteType}`}
					/>
				</div>
			)}
		</div>
	);
};

export default ShadePriceBreakdown;
